import { useMemo, useState } from 'react'
import { Button, Modal, message } from '@/components/ui'
import { PrinterOutlined, SettingOutlined } from '@ant-design/icons'
import type { Attendee } from '../types'
import type { BadgePrintEventData, BadgePrintLog, BadgeTemplate } from '@/types/badgeDesigner'
import { normalizeBadgeTemplate } from '@/lib/badgeTemplate'
import { useBadgePrinterSelection } from '@/hooks/useBadgePrinterSelection'
import PrinterSelector from '@/components/badge_printing/PrinterSelector'
import { toPrintRegistrantPayload, triggerRegistrantBadgePrint } from '@/lib/registrantBadgePrint'

const BADGE_PRINTING_DISABLED_MESSAGE =
  'Badge printing is disabled. Open the Settings tab in Badge Printing and turn on Allow Badge Printing.'

interface BadgePrintModalProps {
  open: boolean
  attendee: Attendee | null
  eventSlug: string
  eventData?: BadgePrintEventData
  badgeTemplate?: BadgeTemplate | null
  isBadgePrintingEnabled?: boolean
  onClose: () => void
  onPrinted?: (
    attendeeId: number,
    updates: { badge_print_logs: BadgePrintLog[]; badge_print_count: number; has_printed_badge: boolean }
  ) => void
}

export default function BadgePrintModal({
  open,
  attendee,
  eventSlug,
  eventData,
  badgeTemplate,
  isBadgePrintingEnabled = true,
  onClose,
  onPrinted,
}: BadgePrintModalProps) {
  const [isPrinting, setIsPrinting] = useState(false)

  const normalizedTemplate = useMemo(
    () => (badgeTemplate ? normalizeBadgeTemplate(badgeTemplate) : null),
    [badgeTemplate]
  )

  const {
    isLoadingPrinters,
    printerOptions,
    selectedPrinterUid,
    selectedPrinter,
    setSelectedPrinterUid,
    detectPrinters,
    hasActivePrinter,
    setupRequired,
    setupMessage,
  } = useBadgePrinterSelection(eventSlug, {
    enabled: open,
    autoDetect: open,
    notifyWhenEmpty: false,
  })

  const handlePrint = async () => {
    if (!attendee) return

    if (!normalizedTemplate) {
      message.warning('Badge template not configured yet')
      return
    }

    if (!isBadgePrintingEnabled) {
      message.warning(BADGE_PRINTING_DISABLED_MESSAGE)
      return
    }

    if (!selectedPrinterUid || !hasActivePrinter) {
      message.warning(setupMessage || 'Pair a print station before printing badges')
      return
    }

    const printerName = selectedPrinter?.printerName || selectedPrinter?.label
    const attendeePayload = {
      id: attendee.id,
      full_name: attendee.full_name,
      email: attendee.email,
      phone: attendee.contact_number,
      qr_value: attendee.qr_code,
      answers: attendee.custom_fields || {},
    }

    setIsPrinting(true)
    try {
      const response = await triggerRegistrantBadgePrint({
        eventSlug,
        registrantId: attendee.id,
        printerUid: selectedPrinter?.printerUid || selectedPrinterUid,
        printerId: selectedPrinter?.printerId,
        stationId: selectedPrinter?.stationId,
        printStationPrinterId: selectedPrinter?.printStationPrinterId,
        printStationId: selectedPrinter?.printStationId,
        printerProfileId: selectedPrinter?.printerProfileId,
        printerName,
        template: normalizedTemplate,
        data: {
          registrant: toPrintRegistrantPayload(attendeePayload),
          event: eventData,
        },
      })

      onPrinted?.(attendee.id, {
        badge_print_logs: response.printLogs || [],
        badge_print_count: response.printedCount || 0,
        has_printed_badge: (response.printedCount || 0) > 0,
      })

      message.success('Badge print job queued')
      onClose()
    } catch (error: any) {
      message.error(error?.message || 'Failed to queue badge print')
    } finally {
      setIsPrinting(false)
    }
  }

  const openBadgePrintingSettings = () => {
    window.location.href = `/events/${eventSlug}/badge_printings?tab=settings`
  }

  return (
    <Modal
      title="Queue Badge Print"
      open={open}
      onClose={onClose}
      footer={null}
      width={520}
      destroyOnClose
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-700">
          <div className="font-semibold">{attendee?.full_name || '-'}</div>
          <div className="text-gray-500">{attendee?.email || '-'}</div>
        </div>

        {!normalizedTemplate && (
          <div className="rounded border border-amber-200 bg-amber-50 text-amber-700 text-sm px-3 py-2">
            No badge template is saved yet. Configure badge design first.
          </div>
        )}

        {!isBadgePrintingEnabled && (
          <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>{BADGE_PRINTING_DISABLED_MESSAGE}</span>
              <Button
                variant="tertiary"
                size="small"
                icon={<SettingOutlined />}
                className="shrink-0"
                onClick={openBadgePrintingSettings}
              >
                Open Badge Printing Settings
              </Button>
            </div>
          </div>
        )}

        <div>
          <label className="text-xs text-gray-600 block mb-1">Agent Printer</label>
          <PrinterSelector
            selectedPrinterUid={selectedPrinterUid}
            printerOptions={printerOptions}
            isLoadingPrinters={isLoadingPrinters}
            onPrinterChange={(value) => setSelectedPrinterUid(value)}
            onRefresh={() => detectPrinters({ notifyOnSuccess: true, notifyWhenEmpty: true })}
            setupRequired={setupRequired}
            setupMessage={setupMessage}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<PrinterOutlined />}
            loading={isPrinting}
            onClick={handlePrint}
            disabled={
              !attendee ||
              !normalizedTemplate ||
              !selectedPrinterUid ||
              !hasActivePrinter ||
              setupRequired ||
              !isBadgePrintingEnabled
            }
          >
            Queue Print Job
          </Button>
        </div>
      </div>
    </Modal>
  )
}
